import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Save,
  X,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import AttorneyInformationCard from "../../components/forms/AttorneyInformationCard"
import { attorneysApi, CreateAttorneyData } from "../../services/attorneysApi"

interface Attorney {
  first_name: string
  last_name: string
  email: string
  phone: string
}

interface FormErrors {
  [key: string]: string[]
}

export default function AddAttorney() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  
  // Phone validation states
  const [phoneChecking, setPhoneChecking] = useState(false)
  const [phoneAvailable, setPhoneAvailable] = useState<boolean | null>(null)
  const [phoneMessage, setPhoneMessage] = useState("")
  const [phoneFormatError, setPhoneFormatError] = useState(false)

  // Email validation states
  const [emailChecking, setEmailChecking] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  const [emailMessage, setEmailMessage] = useState("")

  const [formData, setFormData] = useState<Attorney>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  })

  // Debounced phone availability check
  useEffect(() => {
    if (!formData.phone || formData.phone.length < 10) {
      setPhoneAvailable(null)
      setPhoneMessage("")
      return
    }

    // Format validation is now handled by the child component
    // Only check availability here
    const timer = setTimeout(async () => {
      try {
        setPhoneChecking(true)
        const response = await attorneysApi.checkAttorneyPhoneAvailability(formData.phone)
        setPhoneAvailable(response.available)
        setPhoneMessage(response.message)
      } catch (error) {
        console.error('Phone availability check failed:', error)
        setPhoneAvailable(null)
        setPhoneMessage("Unable to verify phone number")
      } finally {
        setPhoneChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [formData.phone])

  // Debounced email availability check
  useEffect(() => {
    if (!formData.email || formData.email.length < 3) {
      setEmailAvailable(null)
      setEmailMessage("")
      return
    }

    // First validate email format
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    
    if (!emailRegex.test(formData.email)) {
      setEmailAvailable(false)
      setEmailMessage("Please enter a valid email address")
      return
    }

    // Additional email format checks
    const [localPart, domain] = formData.email.split('@')
    if (localPart.length > 64) {
      setEmailAvailable(false)
      setEmailMessage("Email local part cannot exceed 64 characters")
      return
    }
    if (domain.length > 253) {
      setEmailAvailable(false)
      setEmailMessage("Email domain cannot exceed 253 characters")
      return
    }
    if (localPart.length === 0) {
      setEmailAvailable(false)
      setEmailMessage("Email local part cannot be empty")
      return
    }
    if (domain.length === 0) {
      setEmailAvailable(false)
      setEmailMessage("Email domain cannot be empty")
      return
    }

    const timer = setTimeout(async () => {
      try {
        setEmailChecking(true)
        const response = await attorneysApi.checkAttorneyEmailAvailability(formData.email)
        setEmailAvailable(response.available)
        setEmailMessage(response.message)
      } catch (error) {
        console.error('Email availability check failed:', error)
        setEmailAvailable(null)
        setEmailMessage("Unable to verify email")
      } finally {
        setEmailChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [formData.email])

  const handleInputChange = (field: keyof Attorney, value: string) => {
    // Trim trailing spaces for email field
    const processedValue = field === 'email' ? value.replace(/\s+$/, '') : value
    
    setFormData((prev) => ({ ...prev, [field]: processedValue }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: [] }))
    }
    
    // Reset validation states when phone changes
    if (field === 'phone') {
      setPhoneAvailable(null)
      setPhoneMessage("")
    }
    
    // Reset validation states when email changes
    if (field === 'email') {
      setEmailAvailable(null)
      setEmailMessage("")
    }
    
    // Real-time validation for length limits
    if (field === 'first_name' && processedValue.length > 50) {
      setErrors((prev) => ({ ...prev, first_name: ["First name cannot exceed 50 characters"] }))
    } else if (field === 'last_name' && processedValue.length > 50) {
      setErrors((prev) => ({ ...prev, last_name: ["Last name cannot exceed 50 characters"] }))
    } else if (field === 'email') {
      // Clear any existing email errors when email changes
      if (errors.email) {
        setErrors((prev) => ({ ...prev, email: [] }))
      }
    } else if (field === 'phone') {
      // Clear any existing phone errors when phone changes
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: [] }))
      }
    }
  }

  // Check if form can be submitted
  const canSubmit = () => {
    return formData.first_name.trim() && 
           formData.last_name.trim() && 
           formData.email.trim() && 
           formData.phone.trim() &&
           phoneAvailable === true && // Phone must be available
           !phoneFormatError && // Phone format must be valid
           emailAvailable !== false && // Email not invalid (can be true or null)
           !Object.keys(errors).length
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    
    // First name validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = ["First name is required"]
    } else if (formData.first_name.length > 50) {
      newErrors.first_name = ["First name cannot exceed 50 characters"]
    }
    
    // Last name validation
    if (!formData.last_name.trim()) {
      newErrors.last_name = ["Last name is required"]
    } else if (formData.last_name.length > 50) {
      newErrors.last_name = ["Last name cannot exceed 50 characters"]
    }
    
    // Email validation (required)
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = ["Email is required"]
    }
    
    // Phone validation - only check if required, format validation is handled by child component
    if (!formData.phone.trim()) {
      newErrors.phone = ["Phone number is required"]
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    // Check phone availability before submitting
    if (phoneAvailable !== true) {
      setErrors(prev => ({ ...prev, phone: ["Please ensure the phone number is available before submitting"] }))
      return
    }

    setLoading(true)
    setErrors({})

    try {
      // Validate form data with same logic as add-leads.tsx
      const newErrors: FormErrors = {}
      
      // First name validation
      if (!formData.first_name.trim()) {
        newErrors.first_name = ["First name is required"]
      } else if (formData.first_name.length > 50) {
        newErrors.first_name = ["First name cannot exceed 50 characters"]
      }
      
      // Last name validation
      if (!formData.last_name.trim()) {
        newErrors.last_name = ["Last name is required"]
      } else if (formData.last_name.length > 50) {
        newErrors.last_name = ["Last name cannot exceed 50 characters"]
      }
      
      // Email validation (required)
      if (!formData.email.trim()) {
        newErrors.email = ["Email is required"]
      }
      
      // Phone validation - only check if required, format validation is handled by child component
      if (formData.phone && formData.phone.trim()) {
        // Format validation is handled by the child component
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        setLoading(false)
        return
      }

      // Prepare data for API
      const attorneyData: CreateAttorneyData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined, // Send undefined if empty
      }

      // Send POST request to API
      const response = await attorneysApi.createAttorney(attorneyData)

      if (!response.error) {
        // Success - navigate back
        navigate("/app/attorneys")
      } else {
        // Handle API errors - check if response has errors object
        if ((response as any).errors) {
          // Backend returned structured errors
          setErrors((response as any).errors)
        } else {
          // Fallback to general error
          setErrors({ general: [response.message || "Failed to create attorney"] })
        }
      }
    } catch (error: any) {
      console.error("Error saving attorney:", error)
      
      // Try to parse the error message as JSON first (for backend validation errors)
      try {
        const errorData = JSON.parse(error.message)
        if (errorData.errors) {
          // Backend returned structured errors
          setErrors(errorData.errors)
          setLoading(false)
          return
        }
      } catch (parseError) {
        // If we can't parse as JSON, continue with string-based error handling
      }
      
      // Handle different types of errors
      if (error.message?.includes("email") || error.message?.includes("Email")) {
        setErrors({ email: ["This email address is already in use. Please use a different email."] })
      } else if (error.message?.includes("phone")) {
        setErrors({ phone: ["This phone number is already in use"] })
      } else if (error.message?.includes("400")) {
        setErrors({ general: ["Bad Request (400) - Please check your input data"] })
      } else {
        setErrors({ general: ["Failed to create attorney. Please try again."] })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate("/app/attorneys")
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={handleCancel} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Attorneys
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Add New Attorney</h1>
          <p className="text-slate-600">Enter the attorney information below</p>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{errors.general[0]}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <AttorneyInformationCard
            attorneyData={formData}
            errors={errors}
            onDataChange={handleInputChange}
            alwaysEditable={true}
            phoneValidation={{
              phoneChecking,
              phoneAvailable,
              phoneMessage
            }}
            emailValidation={{
              emailChecking,
              emailAvailable,
              emailMessage
            }}
            onPhoneFormatValidationChange={setPhoneFormatError}
          />
          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="border-slate-300 hover:border-slate-400"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !canSubmit()}
              className="bg-slate-900 hover:bg-slate-800 text-white disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? "Saving..." : "Save Attorney"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
} 